import { useState, useCallback, useRef, useEffect } from 'react'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import PaymentModal from '@/components/apply/PaymentModal'
import { track } from '@/lib/analytics'
import type { ApplicationStatusProps } from '@/types/apply'

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

type RazorpayVerifyResponse = {
  razorpay_payment_id: string
  razorpay_subscription_id: string
  razorpay_signature: string
}

let razorpayLoaded = false
function loadRazorpaySdk(): Promise<void> {
  if (razorpayLoaded) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => { razorpayLoaded = true; resolve() }
    script.onerror = () => reject(new Error('Failed to load Razorpay SDK'))
    document.head.appendChild(script)
  })
}

function StatusIcon({ status }: { status: string | null }) {
  if (!status) {
    return (
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-white/5">
        <svg className="h-10 w-10 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    )
  }

  if (status === 'approved') {
    return (
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-500/10">
        <svg className="h-10 w-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    )
  }

  if (status === 'rejected') {
    return (
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-500/10">
        <svg className="h-10 w-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    )
  }

  return (
    <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-yellow-500/10">
      <svg className="h-10 w-10 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  )
}

function StatusMessage({ app }: { app: ApplicationStatusProps['application'] }) {
  if (!app) {
    return (
      <>
        <h1 className="marketing-display text-3xl">Application Not Found</h1>
        <p className="marketing-lead mt-4">
          We couldn't find an application with this token. Please check and try again.
        </p>
      </>
    )
  }

  if (app.status === 'approved') {
    return (
      <>
        <h1 className="marketing-display text-3xl">Application Approved!</h1>
        <p className="marketing-lead mt-4">
          Congratulations! Your application has been approved. You can now access your dashboard.
        </p>
        <div className="mt-8">
          <a href="/dashboard" className="marketing-button-primary">Go to Dashboard</a>
        </div>
      </>
    )
  }

  if (app.status === 'rejected') {
    return (
      <>
        <h1 className="marketing-display text-3xl">Application Not Approved</h1>
        <p className="marketing-lead mt-4">
          Unfortunately, your application was not approved at this time. If you have questions, please contact our support team.
        </p>
        <div className="mt-8">
          <a href="/contact-us" className="marketing-button-secondary">Contact Support</a>
        </div>
      </>
    )
  }

  return (
    <>
      <h1 className="marketing-display text-3xl">Application Under Review</h1>
      <p className="marketing-lead mt-4">
        Your application is being reviewed by our team. We'll notify you once a decision has been made.
      </p>
    </>
  )
}

export default function ApplicationStatus(props: ApplicationStatusProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [retryLoading, setRetryLoading] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  const retryAttemptRef = useRef(0)

  const [mandateModalOpen, setMandateModalOpen] = useState(false)
  const [mandateLoading, setMandateLoading] = useState(false)
  const [mandateError, setMandateError] = useState<string | null>(null)
  // ?mandate=complete / ?mandate=failed is stamped by the Stripe return leg.
  // Read after mount so SSR + hydration match.
  const [mandateFlash, setMandateFlash] = useState<string | null>(null)
  useEffect(() => {
    setMandateFlash(new URLSearchParams(window.location.search).get('mandate'))
  }, [])

  const app = props.application

  // Razorpay embedded-checkout handoff. Parameterized so both the payment retry
  // and the mandate-setup flows share it: each supplies its own verify URL, the
  // POST body to send, and the error/dismiss handlers. On a verified response it
  // reloads; the hosted short_url is never used (it does not work for these subs).
  const openRazorpayModal = useCallback(async (
    subscriptionId: string,
    opts: {
      description: string
      verifyUrl: string
      buildBody: (r: RazorpayVerifyResponse) => Record<string, unknown>
      onError: (message: string) => void
      onDismiss: () => void
    }
  ) => {
    await loadRazorpaySdk()
    const options = {
      key: props.razorpay_key_id,
      subscription_id: subscriptionId,
      name: 'wavbee',
      description: opts.description,
      prefill: { email: app?.applicant_email ?? '' },
      theme: { color: '#ff6a1a' },
      handler: async (response: RazorpayVerifyResponse) => {
        try {
          const verifyRes = await fetch(opts.verifyUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
            body: JSON.stringify(opts.buildBody(response))
          })
          const verifyData = await verifyRes.json()
          if (verifyData.success) {
            window.location.reload()
          } else {
            opts.onError(verifyData.error || 'Payment verification failed.')
          }
        } catch {
          opts.onError('Payment verification failed. Please try again.')
        }
      },
      modal: { ondismiss: opts.onDismiss }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rzp = new (window as any).Razorpay(options)
    rzp.open()
  }, [app?.applicant_email, props.razorpay_key_id])

  const handleRetry = useCallback(async (gateway: string) => {
    retryAttemptRef.current += 1
    track('payment_retry_clicked', {
      data: { application_token: props.token, gateway, attempt_number: retryAttemptRef.current },
    })
    setRetryLoading(true)
    setRetryError(null)
    setModalOpen(false)

    try {
      const res = await fetch(`/applications/${encodeURIComponent(props.token)}/retry_payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({ gateway })
      })
      const data = await res.json()

      if (!res.ok || !data.success) {
        setRetryError(data.error || 'Could not initiate payment.')
        setRetryLoading(false)
        return
      }

      if (data.requires_redirect && data.checkout_url) {
        window.location.href = data.checkout_url
        return
      }

      if (data.requires_modal && data.subscription_id) {
        await openRazorpayModal(data.subscription_id, {
          description: 'Application Verification',
          verifyUrl: '/applications/verify_razorpay_payment',
          buildBody: (r) => ({
            payment_id: r.razorpay_payment_id,
            subscription_id: r.razorpay_subscription_id,
            signature: r.razorpay_signature,
            application_token: props.token
          }),
          onError: (message) => { setRetryError(message); setRetryLoading(false) },
          onDismiss: () => {
            // Mark the un-authorized sub abandoned so it stops reading as a live
            // awaiting subscription; the retry button stays available.
            void fetch(`/applications/${encodeURIComponent(props.token)}/abandon_payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() }
            }).catch(() => { /* best-effort */ })
            setRetryLoading(false)
          }
        })
        return
      }

      // Fake-mode immediate success — refresh to show updated state.
      window.location.reload()
    } catch {
      setRetryError('Network error. Please try again.')
      setRetryLoading(false)
    }
  }, [openRazorpayModal, props.token])

  // Paid applicant whose mandate was revoked: mint a fresh authorization link on
  // demand (either gateway). No charge — the verification fee is already paid.
  const handleMandateSetup = useCallback(async (gateway: string) => {
    setMandateLoading(true)
    setMandateError(null)
    setMandateModalOpen(false)

    try {
      const res = await fetch(`/applications/${encodeURIComponent(props.token)}/setup_mandate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({ gateway })
      })
      const data = await res.json()

      if (!res.ok || !data.success) {
        setMandateError(data.error || 'Could not start the mandate setup.')
        setMandateLoading(false)
        return
      }

      // Stripe: hosted setup-mode Checkout redirect.
      if (data.requires_redirect && data.checkout_url) {
        window.location.href = data.checkout_url
        return
      }

      // Razorpay: authorize the mandate in the embedded modal (the hosted
      // short_url does not work for these parked subs), then verify server-side.
      if (data.requires_modal && data.subscription_id) {
        await openRazorpayModal(data.subscription_id, {
          description: 'Payment Authorization',
          verifyUrl: `/applications/${encodeURIComponent(props.token)}/verify_mandate_setup`,
          buildBody: (r) => ({
            razorpay_payment_id: r.razorpay_payment_id,
            razorpay_subscription_id: r.razorpay_subscription_id,
            razorpay_signature: r.razorpay_signature
          }),
          onError: (message) => { setMandateError(message); setMandateLoading(false) },
          // NOT abandon_payment — that endpoint is for the verification-payment
          // state machine and must never run against a payment_success app.
          onDismiss: () => { setMandateModalOpen(false); setMandateLoading(false) }
        })
        return
      }

      // already-active / fake completion — refresh to show the updated state.
      window.location.reload()
    } catch {
      setMandateError('Network error. Please try again.')
      setMandateLoading(false)
    }
  }, [openRazorpayModal, props.token])

  const showRetry = !!app && app.can_retry_payment
  const showMandateSetup = props.needs_mandate_setup && !showRetry

  return (
    <section className="marketing-hero-section">
      <div className="container mx-auto px-6 py-20">
        <ScrollReveal direction="up">
          <div className="mx-auto max-w-lg text-center">
            <StatusIcon status={app?.status ?? null} />
            <StatusMessage app={app} />

            {showRetry && (
              <div className="mt-6 rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-5 py-4 text-left">
                <p className="text-sm font-semibold text-yellow-200">Payment required to submit your application</p>
                <p className="mt-1 text-xs text-yellow-100/80">
                  Your application is saved but won't be reviewed until the verification charge clears.
                  You can complete it now with either gateway.
                </p>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  disabled={retryLoading}
                  className="marketing-button-primary mt-4 inline-flex w-full items-center justify-center"
                >
                  {retryLoading ? 'Processing…' : 'Complete payment'}
                </button>
                {retryError && (
                  <p className="mt-3 text-xs text-red-300">{retryError}</p>
                )}
              </div>
            )}

            {showMandateSetup && (
              <div className="mt-6 rounded-xl border border-[#ff6a1a]/30 bg-[#ff6a1a]/10 px-5 py-4 text-left">
                <p className="text-sm font-semibold text-[#ff6a1a]">Set up your payment mandate</p>
                <p className="mt-1 text-xs text-white/70">
                  Your verification fee is already paid — you will not be charged again now.
                  Authorize a payment mandate so we can activate your subscription once your
                  application is approved. You can use either a card or UPI / Netbanking.
                </p>
                {mandateFlash === 'complete' && (
                  <p className="mt-3 text-xs text-green-300">Your payment mandate is set up. Thank you!</p>
                )}
                {mandateFlash === 'failed' && (
                  <p className="mt-3 text-xs text-red-300">
                    We couldn&apos;t complete the authorization. Please try again.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setMandateModalOpen(true)}
                  disabled={mandateLoading}
                  className="marketing-button-primary mt-4 inline-flex w-full items-center justify-center"
                >
                  {mandateLoading ? 'Processing…' : 'Set up payment mandate'}
                </button>
                {mandateError && (
                  <p className="mt-3 text-xs text-red-300">{mandateError}</p>
                )}
              </div>
            )}

            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.04] px-6 py-4">
              <p className="text-sm text-white/50">Application Token</p>
              <p className="mt-1 font-mono text-lg text-[#ff6a1a]">{props.token}</p>
            </div>

            <div className="mt-8">
              <a href="/" className="marketing-button-secondary">Back to Home</a>
            </div>
          </div>
        </ScrollReveal>

        {modalOpen && (
          <PaymentModal
            gateways={props.gateways}
            onClose={() => setModalOpen(false)}
            onSubmit={handleRetry}
            loading={retryLoading}
          />
        )}

        {mandateModalOpen && (
          <PaymentModal
            gateways={props.mandate_gateways}
            variant="mandate"
            onClose={() => setMandateModalOpen(false)}
            onSubmit={handleMandateSetup}
            loading={mandateLoading}
          />
        )}
      </div>
    </section>
  )
}
